import { Mode } from '../config/general.config.js';
import { SortDetails, SortType } from './sortDetails.js';

export interface IFilter {
	locationStatuses?: string;
	companyStatuses?: string;
	locations?: string[];
	primarySectors?: string[];
	primarySubSectors?: string[];
	secondarySectors?: string[];
	secondarySubSectors?: string[];
	tags?: string[];
	indications?: string[];
	isBlueKnight?: boolean;
	isCrossSector?: boolean;
	isQFCWinner?: boolean;
	isNewInLastQuarter?: boolean;
	currentRdStage?: string;
	isNewInLast90Days?: boolean;
	/** This is a bit of a bastardization of filter attributes, since it is not meant to be used with any others.
	 * It was introduced simply to statisfy ADJQ-2047 in a quick & dirty way.
	 */
	showXNewCompanies?: number;
	isFollowedCompaniesOnly?: boolean;
	isFollowedAssetsOnly?: boolean;
	isMyJPALCompaniesOnly?: boolean;
	sort?: SortDetails;
	focusAreas?: string[];
	therapeuticAreas?: string[];
	developmentStages?: string[];
	mode: Mode;
}

export class Filter implements IFilter {
	public static equals(someFilter: IFilter, otherFilter: IFilter): boolean {
		// FUTURE - the order is static, but there are cases where this will be wrong,
		// e.g. tags of null or empty array are the same, but this won't think so.
		// We haven't seen any evidence of this happening, but it is a risk.

		// Stringified JSON is an efficient and scalable way to compare complex objects
		// provided that the order of properties is static. See https://stackoverflow.com/a/1144249
		// and https://www.mattzeunert.com/2016/01/28/javascript-deep-equal.html
		return JSON.stringify(someFilter) === JSON.stringify(otherFilter);
	}

	public locationStatuses?: string;
	public companyStatuses?: string;
	public locations?: string[];
	public primarySectors?: string[];
	public primarySubSectors?: string[];
	public secondarySectors?: string[];
	public secondarySubSectors?: string[];
	public tags?: string[];
	public indications?: string[];
	public isBlueKnight?: boolean;
	public isCrossSector?: boolean;
	public isQFCWinner?: boolean;
	/** We'd have to dig up history as to why this was introduced in addition to isNewInLast90Days. It seems like it can lead to UX confusion. */
	public isNewInLastQuarter?: boolean;
	public currentRdStage?: string;
	/** We'd have to dig up history as to why this was introduced in addition to isNewInLastQuarter. It seems like it can lead to UX confusion. */
	public isNewInLast90Days?: boolean;
	/** Introduced as a fallback if isNewInLast90Days doesn't return any results */
	public showXNewCompanies?: number;
	public isFollowedCompaniesOnly?: boolean;
	public isFollowedAssetsOnly?: boolean;
	public isMyJPALCompaniesOnly?: boolean;

	public focusAreas?: string[];
	public therapeuticAreas?: string[];
	public developmentStages?: string[];
	public sort: SortDetails = SortDetails.RankedSort;
	public mode: Mode;

	public constructor(filter?: IFilter) {
		this.mode = filter?.mode ?? Mode.Company;
		this.sort = filter?.sort ?? SortDetails.AlphaSort; // this will be automatically set to Ranked when initiating a keyword search
		this.locations = filter?.locations ?? [];
		this.primarySectors = filter?.primarySectors ?? [];
		this.primarySubSectors = filter?.primarySubSectors ?? [];
		this.secondarySectors = filter?.secondarySectors ?? [];
		this.secondarySubSectors = filter?.secondarySubSectors ?? [];
		this.tags = filter?.tags ?? [];
		this.indications = filter?.indications ?? [];
		this.currentRdStage = filter?.currentRdStage ?? '';
		this.locationStatuses = filter?.locationStatuses ?? '';
		this.companyStatuses = filter?.companyStatuses ?? '';
		this.focusAreas = filter?.focusAreas ?? [];
		this.therapeuticAreas = filter?.therapeuticAreas ?? [];
		this.developmentStages = filter?.developmentStages ?? [];
		this.isBlueKnight = filter?.isBlueKnight ?? false;
		this.isCrossSector = filter?.isCrossSector ?? false;
		this.isQFCWinner = filter?.isQFCWinner ?? false;
		this.isNewInLastQuarter = filter?.isNewInLastQuarter ?? false;
		this.isNewInLast90Days = filter?.isNewInLast90Days ?? false;
		this.showXNewCompanies = filter?.showXNewCompanies ?? 0;
		this.isFollowedCompaniesOnly = filter?.isFollowedCompaniesOnly ?? false;
		this.isFollowedAssetsOnly = filter?.isFollowedAssetsOnly ?? false;
		this.isMyJPALCompaniesOnly = filter?.isMyJPALCompaniesOnly ?? false;
	}

	public static get emptyFilter(): Filter {
		return new Filter();
	}

	/** Note: the parameter defaults aren't great, since they should be influenced by the presence of a search predicate (which isn't available in this context) */
	public addSort(
		type = SortType.Alpha,
		direction: 'asc' | 'desc' = 'asc',
		field = null
	) {
		this.sort = {
			type,
			direction,
			field,
		};
	}

	public static get emptyAlphaSortFilter(): Filter {
		const empty = this.emptyFilter;
		empty.sort = SortDetails.AlphaSort;
		return empty;
	}

	public static get emptyDateSortFilter(): Filter {
		const empty = this.emptyFilter;
		empty.sort = SortDetails.DateSort;
		return empty;
	}

	public get isUserSpecific(): boolean {
		return (
			this.isFollowedCompaniesOnly ||
			this.isMyJPALCompaniesOnly ||
			this.isFollowedAssetsOnly
		);
	}

	public toString(): string {
		return JSON.stringify(this);
	}

	public isShowAll(): boolean {
		// NOTE: The various filters available in the UI treat NONE or empty as ALL.
		return (
			(!this.locations || this.locations.length === 0) &&
			(!this.locationStatuses || this.locationStatuses === '') &&
			(!this.locations || this.locations.length === 0) &&
			(!this.companyStatuses || this.companyStatuses === '') &&
			(!this.primarySectors || this.primarySectors.length === 0) &&
			(!this.primarySubSectors || this.primarySubSectors.length === 0) &&
			(!this.secondarySectors || this.secondarySectors.length === 0) &&
			(!this.secondarySubSectors ||
				this.secondarySubSectors?.length === 0) &&
			(!this.indications || this.indications.length === 0) &&
			(!this.therapeuticAreas || this.therapeuticAreas.length === 0) &&
			(!this.focusAreas || this.focusAreas.length === 0) &&
			(!this.developmentStages || this.developmentStages.length === 0) &&
			(!this.tags || this.tags.length === 0) &&
			!this.isBlueKnight &&
			!this.isCrossSector &&
			!this.isQFCWinner &&
			!this.isNewInLastQuarter &&
			!this.currentRdStage &&
			!this.isNewInLast90Days &&
			!this.isFollowedAssetsOnly &&
			!this.isFollowedCompaniesOnly &&
			!this.isMyJPALCompaniesOnly
		);
	}

	public clear(requireOneSector: boolean = false): void {
		this.locationStatuses = '';
		this.companyStatuses = '';
		this.locations = [];
		this.indications = [];

		// When a filter is in singleSector mode (for assets on the explore page),
		// it will always have exactly one primary sector. In this case, we don't
		// clear it but leave the current one.
		if (requireOneSector) {
			this.primarySectors =
				this.primarySectors?.length > 0 && this.primarySectors[0]
					? [this.primarySectors[0]]
					: [];
		} else {
			this.primarySectors = [];
		}
		this.primarySubSectors = [];
		this.secondarySectors = [];
		this.secondarySubSectors = [];
		this.developmentStages = [];
		this.therapeuticAreas = [];
		this.focusAreas = [];
		this.tags = undefined;
		this.isBlueKnight = undefined;
		this.isCrossSector = undefined;
		this.isQFCWinner = undefined;
		this.isNewInLastQuarter = undefined;
		this.currentRdStage = undefined;
		this.isNewInLast90Days = undefined;
		this.showXNewCompanies = undefined;
		this.isFollowedAssetsOnly = false;
		this.isFollowedCompaniesOnly = undefined;
		this.isMyJPALCompaniesOnly = undefined;
		this.sort = SortDetails.RankedSort;
	}

	/** Returns a deep copy of this filter */
	public clone(): Filter {
		const copy = Filter.emptyFilter;
		Object.assign(copy, this);
		copy.locations = [...this.locations];
		copy.primarySectors = [...this.primarySectors];
		copy.primarySubSectors = [...this.primarySubSectors];
		copy.secondarySectors = [...this.secondarySectors];
		copy.secondarySubSectors = [...this.secondarySubSectors];
		copy.indications = [...this.indications];
		copy.focusAreas = [...this.focusAreas];
		copy.developmentStages = [...this.developmentStages];
		copy.therapeuticAreas = [...this.therapeuticAreas];
		copy.tags = this.tags ? [...this.tags] : undefined;
		return copy;
	}

	public serializeToKeyValuePairs(): string[] {
		return Filter.toKeyValuePairs(this, Filter.emptyFilter);
	}

	public static toKeyValuePairs(
		obj: unknown,
		defaultValues: unknown = {}
	): string[] {
		const keyValuePairs = [];

		for (const [key, value] of Object.entries(obj)) {
			const isArray = Array.isArray(value);
			if (
				(value !== undefined &&
					!isArray &&
					value !== defaultValues[key]) ||
				(isArray && value.length !== 0)
			) {
				if (typeof value === 'boolean' && !value) {
					continue;
				}

				if (value && !isArray && typeof value === 'object') {
					keyValuePairs[key] = {
						value: Filter.toKeyValuePairs(value),
						isArray: false,
						isObject: true,
					};
					continue;
				}

				keyValuePairs[key] = {
					value: value,
					isArray: isArray,
					isObject: false,
				};
			}
		}

		return keyValuePairs;
	}
}
